import {
  hashKey,
  InfiniteData,
  QueryKey,
  useInfiniteQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';

import { useLocale } from '../../../../hooks/use-locale.hook';
import { SpaceBrowserPagination } from '../../types/data-room.types';
import { keepWithinScope } from './keep-within-scope.util';

export const ACTIVITY_PAGE_SIZE = 20;

// What every Activity list answers with: one page of rows and the count of all of them.
export type ActivityPage = { count: number; records: unknown[] };

type Row<Page extends ActivityPage> = Page['records'][number];

/** One Activity list as its table draws it: the rows so far, how many there are, and the pager. */
export type ActivityPages<Page extends ActivityPage> = {
  count: number;
  firstPage: Page | undefined;
  isError: boolean;
  isPending: boolean;
  pagination: SpaceBrowserPagination;
  retry: () => void;
  rows: Row<Page>[];
};

// One empty list for every row type, so a table with nothing loaded keeps the same rows prop.
const NO_ROWS: never[] = [];

type Props<Page extends ActivityPage> = {
  // The caller owns the route and the filters, so both stay where the request is built.
  fetchPage: (skip: number) => Promise<Page>;
  queryKey: QueryKey;
  // What tells one row from another. A module-level function, so the list is not rebuilt on every
  // render. Typed from the page `fetchPage` returns, never the other way round.
  rowKey: (row: NoInfer<Row<Page>>) => string;
  // The key parts naming the company: another filter's rows may stand in, another company's never.
  scope: readonly unknown[];
};

/**
 * Pages are asked for by position while the server orders by live activity, so a row that moves
 * between two "Load more"s arrives a second time. The first one shown stands; the later copy is
 * dropped rather than drawn twice under one key.
 */
const uniqueRows = <RowType>(rows: RowType[], rowKey: (row: RowType) => string): RowType[] => {
  const seen = new Set<string>();

  return rows.filter((row) => {
    const key = rowKey(row);

    if (seen.has(key)) return false;

    seen.add(key);

    return true;
  });
};

const countLoaded = (pages: ActivityPage[]): number =>
  pages.reduce((count, page) => count + page.records.length, 0);

// Back to the first page; the rest is fetched again on demand.
const toFirstPage = <Page>(
  data: InfiniteData<Page, number> | undefined,
): InfiniteData<Page, number> | undefined =>
  data && { pageParams: data.pageParams.slice(0, 1), pages: data.pages.slice(0, 1) };

/**
 * An Activity list 20 rows at a time, paged the way the Workspaces browser pages a folder: each
 * "Load more" asks for the next page under the rows already shown, and "Show less" folds back to
 * the first. A new window, filter or order starts from the first page again.
 */
export const useActivityPages = <Page extends ActivityPage>({
  fetchPage,
  queryKey: freshKey,
  rowKey,
  scope,
}: Props<Page>): ActivityPages<Page> => {
  const queryClient = useQueryClient();
  const { messagesLocale } = useLocale();
  // The tabs build the key afresh on every render; it names the same query while its hash holds.
  const queryHash = hashKey(freshKey);
  const queryKey = useMemo(() => JSON.parse(queryHash) as QueryKey, [queryHash]);
  // When this query became the one shown; a failure older than that is the cache's.
  const { at: shownSince } = useMemo(() => ({ at: Date.now(), queryKey }), [queryKey]);

  const {
    data,
    error,
    errorUpdatedAt,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetchingNextPage,
    isPending,
    isPlaceholderData,
    refetch,
  } = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => fetchPage(pageParam),
    getNextPageParam: (lastPage, pages) => {
      const loaded = countLoaded(pages);

      // The count and the rows are read apart, so a page can come back empty under a count that
      // still promises more; offering "Load more" again would fetch nothing, forever.
      return lastPage.records.length && loaded < lastPage.count ? loaded : undefined;
    },
    initialPageParam: 0,
    placeholderData: keepWithinScope(scope),
  });

  // With nothing shown the tab says so itself; a failed later page keeps the rows on screen, so it
  // is told once.
  const hasRows = !!data?.pages.length;

  useEffect(() => {
    if (error && hasRows && errorUpdatedAt >= shownSince) {
      toast.error(messagesLocale.ACTIVITY_LOAD_FAILED);
    }
  }, [error, errorUpdatedAt, hasRows, messagesLocale, shownSince]);

  // One list across the pages, rebuilt only when a page arrives.
  const list = useMemo(() => {
    const pages = data?.pages;

    if (!pages?.length) return null;

    const { count } = pages[pages.length - 1];

    return {
      count,
      firstPage: pages[0],
      pageCount: pages.length,
      remainingCount: Math.max(0, count - countLoaded(pages)),
      rows: uniqueRows(pages.flatMap((page) => page.records) as Row<Page>[], rowKey),
    };
  }, [data, rowKey]);

  const loadMore = useCallback(() => void fetchNextPage(), [fetchNextPage]);

  const showLess = useCallback(
    () => queryClient.setQueryData<InfiniteData<Page, number>>(queryKey, toFirstPage),
    [queryClient, queryKey],
  );

  const retry = useCallback(() => void refetch(), [refetch]);

  // The stand-in pages are another query's, so none of them is continued or folded; the bar keeps
  // the sizes it showed for those very rows, only disabled, so it does not drop out and jump back.
  const settledSizes = useRef({ canShowLess: false, nextPageSize: 0, remainingCount: 0 });

  if (!isPlaceholderData) {
    const remaining = list?.remainingCount ?? 0;

    settledSizes.current = {
      canShowLess: !hasNextPage && (list?.pageCount ?? 0) > 1,
      nextPageSize: hasNextPage ? Math.min(ACTIVITY_PAGE_SIZE, remaining) : 0,
      remainingCount: remaining,
    };
  }

  const { canShowLess, nextPageSize, remainingCount } = settledSizes.current;

  const pagination = useMemo(
    (): SpaceBrowserPagination => ({
      canShowLess,
      isLoadingMore: isFetchingNextPage,
      isReloading: isPlaceholderData,
      loadMore,
      nextPageSize,
      remainingCount,
      showLess,
    }),
    [
      canShowLess,
      isFetchingNextPage,
      isPlaceholderData,
      loadMore,
      nextPageSize,
      remainingCount,
      showLess,
    ],
  );

  return {
    count: list?.count ?? 0,
    firstPage: list?.firstPage,
    // The first page failed: there is nothing to show, not an empty list.
    isError: isError && !hasRows,
    isPending,
    pagination,
    retry,
    rows: list?.rows ?? NO_ROWS,
  };
};
